import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useApiListAuthSession } from '../../../../../api-new/state/auth'
import { IconAuthLogin, IconLogout } from '../../../../../shared/icon/icon'
import { FleetPage, RollupTiles } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../shared/menu/menu'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { formatAgo, formatDateTime, serverMessage } from '../../../../../shared/sso-workspace/format'
import { OTHER_SESSIONS_STAY, signOutSessionBody, signedInWith } from '../../../org/login-session/content/login-session'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { SECTION } from '../../../../../shared/navigation/sections'

const METHOD_FILTERS = [
  { value: '', label: 'Any sign-in' },
  { value: 'sso', label: 'Single sign-on' },
  { value: 'pin', label: 'PIN' },
  { value: 'lms', label: 'LMS launch' },
  { value: 'account', label: 'EducationXR sign-in' }
]

const methodOf = s => (s.lmsLaunch ? 'lms' : s.providerID ? 'sso' : s.isPinAuthenticated ? 'pin' : 'account')

const device = s => [s.deviceName, s.deviceOS || s.devicePlatform].filter(Boolean).join(' · ') || s.deviceType || ''
const app = s => [s.app, s.appVersion].filter(Boolean).join(' ')
const where = s => s.ipLocation || s.ipAddress || ''

// Every signed-in session across the platform (staff), in the workspace's
// language (audit F22 and section 4): who, which organization, how they
// signed in ("Signed in with": the connection for single sign-on), when,
// where from, on what, and End session behind a confirmation. One row per
// session (auth folds a session's organization switches, and the app and
// any admin tab opened from it, into one), and ending it ends all of it.
// Your own session is marked and can't be ended from here.
export const AuthSessionList = () => {
  const apiListAuthSession = useApiListAuthSession()

  const [rows, setRows] = useState(undefined)
  const [method, setMethod] = useState('')
  const [ending, setEnding] = useState(null)
  const [endError, endFailure, setEndError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')

  const load = async () => {
    try { setRows((await apiListAuthSession.get()) || []) } catch (err) { setRows(null) }
  }

  const tableRef = useRef()

  const visible = useMemo(() => (Array.isArray(rows) && method ? rows.filter(r => methodOf(r) === method) : rows), [rows, method])

  const counts = useMemo(() => {
    const c = { all: 0, sso: 0, people: new Set() }
    ;(rows || []).forEach(r => { c.all += 1; if (r.providerID) c.sso += 1; if (r.email) c.people.add(r.email) })
    return c
  }, [rows])

  const figure = n => (rows === undefined ? '…' : rows === null ? 'Unavailable' : String(n))

  const handleEnd = async () => {
    setBusy(true)
    setEndError(null)
    try {
      await apiListAuthSession.delete(ending.id)
    } catch (err) {
      setEndError(serverMessage(err, "The session couldn't be ended."), err)
      setBusy(false)
      return
    }
    setBusy(false)
    setStatus(`Ended ${ending.email}'s session. They'll need to sign in again wherever it was in use.`)
    setEnding(null)
    await load()
  }

  const gridRows = useMemo(
    () =>
      Array.isArray(visible)
        ? visible.map(s => ({ ...s, signedInWith: signedInWith(s), where: where(s), appLabel: app(s), device: device(s) }))
        : visible,
    [visible]
  )
  const gridList = useGridList(gridRows)

  return (
    <BreadcrumbView Icon={SECTION.authSessions.Icon} text={SECTION.authSessions.text}>
      <FleetPage
        title="Auth sessions"
        purpose="Everyone signed in to EducationXR right now: how they signed in, where from and on what. End a session to sign them out everywhere it's in use.">
        <RollupTiles label="sessions at a glance" tiles={[
          { key: 'all', label: 'Sessions', value: figure(counts.all) },
          { key: 'people', label: 'People', value: figure(counts.people.size) },
          { key: 'sso', label: 'Through single sign-on', value: figure(counts.sso) }
        ]} />
        <div className="sw-toolbar">
          <select className="form-select form-select-sm" aria-label="Signed in with" value={method} onChange={e => setMethod(e.target.value)}>
            {METHOD_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {status && <p className="sw-status" role="status" aria-live="polite">{status}</p>}
        </div>
        {rows === null && <p className="text-danger small" role="alert">The sessions couldn't be loaded. Reload to try again.</p>}
        <FleetGrid title="sessions" tableRef={tableRef} exportName="sessions">
          <TableAgClient
            ref={tableRef}
            apiList={gridList}
            onReady={load}
            sortCol="createdAt"
            sortDesc
            emptyState={{ Icon: IconAuthLogin, title: 'Nobody is signed in', message: 'Nobody is signed in right now.' }}>
            <ColumnFormat field="email" header="Person" render={personCell} sortable />
            <ColumnText field="organization" header="Organization" sortable />
            <ColumnFormat field="signedInWith" header="Signed in with" render={signedInWithCell} sortable />
            <ColumnFormat field="createdAt" header="Started" render={agoCell} sortable />
            <ColumnFormat field="lastActiveAt" header="Last active" render={agoCell} sortable />
            <ColumnFormat field="expiresOn" header="Expires" render={dateTimeCell} sortable />
            <ColumnFormat field="where" header="Where" render={whereCell} sortable />
            <ColumnFormat field="appLabel" header="App" render={unknownCell} sortable />
            <ColumnFormat field="device" header="Device" render={unknownCell} sortable />
            <ColumnMenu
              menu={s =>
                s.isCurrentSession
                  ? <span className="sw-hint small text-nowrap">This session</span>
                  : <Menu small>
                      <MenuItem Icon={IconLogout} text="end session" pin show onClick={() => { setEndError(null); setEnding(s) }} />
                    </Menu>}
            />
          </TableAgClient>
        </FleetGrid>
      </FleetPage>

      <ConfirmDialog
        open={Boolean(ending)}
        title={ending ? `End ${ending.email}'s session?` : ''}
        body={ending ? `${signOutSessionBody(ending.email)} ${OTHER_SESSIONS_STAY}` : ''}
        confirmText="End session"
        busy={busy}
        error={endError}
        failure={endFailure}
        onConfirm={handleEnd}
        onCancel={() => setEnding(null)}
      />
    </BreadcrumbView>
  )
}

const personCell = row =>
  row.data
    ? <span>{row.data.email}{row.data.isCurrentSession && <> <Pill tone="info">You, now</Pill></>}</span>
    : ''

const signedInWithCell = row =>
  row.data
    ? row.data.providerID
      ? <Link to={`/admin/sso/connections/${row.data.providerID}`}>{row.value}</Link>
      : row.value
    : ''

const agoCell = row => (row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')

const dateTimeCell = row => formatDateTime(row.value)

const whereCell = row => (row.data ? <span title={row.data.ipAddress}>{row.value || <span className="sw-hint">Unknown</span>}</span> : '')

const unknownCell = row => row.value || <span className="sw-hint">Unknown</span>
